const { getByIds: getLocationsByIds } = require('./../location')
const { findById: findShipmentById } = require('./shipment-find')
const { parseCoordinates } = require('./../location/tools/coordinate-helper')
const { SHIPMENT_GPS_FAIL_REASONS } = require('./constants')
const toDocIdProperties = require('./tools/to-doc-id-properties')
const toLocationProperties = require('./tools/to-location-properties')
const distance = require('@turf/distance').default
const { point } = require('@turf/helpers')

const MAXIMUM_DELIVERY_RADIUS_KM = 2

// FE sends {lat,lng}; server stores {lat,long}. Accept either, normalize.
const normalizePosition = (p) => {
  if (!p) return null
  const lat = p.lat
  const long = p.long != null ? p.long : p.lng
  return (lat == null || long == null) ? null : { lat, long }
}

// `state` is required because this function is exposed through wrap-api,
// which auto-prepends `state` to every external call. Removing it shifts
// the caller's `from` into `state` and produces NaN distances / "coordinates
// must contain numbers" errors on the client.
function calculateDistance (state, from, to) {
  return distance(point([from.long, from.lat]), point([to.long, to.lat]), { units: 'kilometers' })
}

// `found` distinguishes a missing location from one without usable coordinates; each caller applies its own contract.
async function readLocationCoordinates (state, locationId) {
  const [location] = await getLocationsByIds(state, [locationId])
  if (!location) return { found: false, coordinates: null }
  return {
    found: true,
    coordinates: normalizePosition(parseCoordinates(location.additionalData.latLng))
  }
}

async function resolveDestination (state, shipment, knownDestinationCoordinates) {
  const known = normalizePosition(knownDestinationCoordinates)
  if (known) return known

  const { found, coordinates } = await readLocationCoordinates(state, shipment.destination.id)
  if (!found) {
    const error = new Error('Shipment destination not found.')
    error.status = 404
    throw error
  }
  if (!coordinates) {
    const error = new Error('Missing destination coordinates.')
    error.status = 404
    throw error
  }
  return coordinates
}

// Normalize the optional accuracy arg into a finite, non-negative number
// of meters. Anything else (null, undefined, NaN, negatives) returns
// `undefined` so we don't write garbage onto the snapshot.
const normalizeAccuracy = (a) => {
  if (a == null) return undefined
  const n = Number(a)
  return Number.isFinite(n) && n >= 0 ? n : undefined
}

async function addGpsValidationFailComment (state, snapshotId, reason, position, accuracy) {
  if (!Object.values(SHIPMENT_GPS_FAIL_REASONS).includes(reason)) {
    const error = new Error('Invalid gps validation fail reason')
    error.status = 400
    throw error
  }

  const driverPosition = normalizePosition(position)
  if (!driverPosition) {
    const error = new Error('Missing driver coordinates for GPS fail comment.')
    error.status = 400
    throw error
  }

  const driverAccuracy = normalizeAccuracy(accuracy)
  const snapshot = await state.shipmentsDb.get(snapshotId)
  const date = new Date().toJSON()
  snapshot.updatedAt = date
  const failComment = { reason, date, position: driverPosition }
  if (driverAccuracy !== undefined) failComment.accuracy = driverAccuracy
  snapshot.gpsValidationFailComment = failComment
  await state.shipmentsDb.put(snapshot)

  return { _id: snapshotId, reason, date, position: driverPosition, accuracy: driverAccuracy }
}

async function gpsValidation (state, shipmentId, currentCoordinates, knownDestinationCoordinates, accuracy) {
  const driverPosition = normalizePosition(currentCoordinates)
  if (!driverPosition) {
    const error = new Error('Missing driver coordinates for GPS validation.')
    error.status = 400
    throw error
  }

  const shipment = await findShipmentById(state, shipmentId)
  if (shipment.isGpsAuthenticated) {
    const error = new Error('GPS validation already approved for current shipment.')
    error.status = 400
    throw error
  }

  const destinationCoordinates = await resolveDestination(state, shipment, knownDestinationCoordinates)
  const distanceKm = calculateDistance(state, driverPosition, destinationCoordinates)
  const driverAccuracy = normalizeAccuracy(accuracy)

  const snapshot = await state.shipmentsDb.get(shipment.snapshotId)
  const date = new Date().toJSON()
  snapshot.updatedAt = date

  if (distanceKm > MAXIMUM_DELIVERY_RADIUS_KM) {
    // Flagged: driver coords land on the follow-up gpsValidationFailComment,
    // so we deliberately don't also set gpsValidation here.
    snapshot.isGpsAuthenticated = false
    await state.shipmentsDb.put(snapshot)
    const error = new Error(`GPS validation failed, current distance from destination is ${distanceKm.toFixed(2)} km. Maximum distance allowed is ${MAXIMUM_DELIVERY_RADIUS_KM} km.`)
    error.status = 400
    throw error
  }

  snapshot.isGpsAuthenticated = true
  const gpsValidationRecord = { position: driverPosition, date, distance: distanceKm }
  if (driverAccuracy !== undefined) gpsValidationRecord.accuracy = driverAccuracy
  snapshot.gpsValidation = gpsValidationRecord
  await state.shipmentsDb.put(snapshot)

  return {
    message: 'GPS validation succeeded',
    comment: `GPS validation succeeded, distance from destination was ${distanceKm.toFixed(2)} km.`
  }
}

// Self-pickup hand-off happens at the warehouse (origin), not the destination. PRD GEN-499 Req 5; issue 23253.
const HANDOFF_LOCATION_RADIUS_KM = 0.05 // ~50m

// Callers pass the snapshot id only. The origin (the warehouse to compare
// against) and the status are parsed from the id itself, the same parse otp.js
// uses, so a caller cannot choose the reference location or write onto a
// SENT/RECEIVED delivery snapshot.
async function captureHandoffLocation (state, snapshotId, currentCoordinates, accuracy) {
  const position = normalizePosition(currentCoordinates)
  if (!position) {
    const error = new Error('Missing hand-off coordinates.')
    error.status = 400
    throw error
  }

  const { origin, status } = toDocIdProperties(snapshotId)
  if (status !== 'packed') {
    const error = new Error('Hand-off location can only be recorded on a packed snapshot.')
    error.status = 400
    throw error
  }

  const snapshot = await state.shipmentsDb.get(snapshotId)

  const { coordinates: originCoordinates } = await readLocationCoordinates(state, toLocationProperties(origin).id)
  const distanceKm = originCoordinates ? calculateDistance(state, position, originCoordinates) : null
  const isWithinHandoffRadius = distanceKm != null && distanceKm <= HANDOFF_LOCATION_RADIUS_KM

  const date = new Date().toJSON()
  snapshot.updatedAt = date

  // Recorded on the verification fields the LMD page already renders, so the hand-off is visible for review.
  // Unlike the driver gpsValidation it never rejects on distance, only flags a far hand-off.
  const handoffLocation = { position, date }
  const handoffAccuracy = normalizeAccuracy(accuracy)
  if (handoffAccuracy !== undefined) handoffLocation.accuracy = handoffAccuracy
  if (distanceKm != null) handoffLocation.distance = distanceKm
  snapshot.isGpsAuthenticated = isWithinHandoffRadius

  if (!originCoordinates) {
    // Nothing to compare against: never blocks, flags the hand-off with a warehouse-specific reason.
    snapshot.gpsValidationFailComment = {
      reason: SHIPMENT_GPS_FAIL_REASONS.warehouse_location_coordinates_missing,
      date,
      position
    }
    if (handoffAccuracy !== undefined) snapshot.gpsValidationFailComment.accuracy = handoffAccuracy
  } else {
    snapshot.gpsValidation = handoffLocation
  }
  await state.shipmentsDb.put(snapshot)

  return { message: 'Hand-off location recorded', handoffLocation, isFlagged: !isWithinHandoffRadius }
}

module.exports = { calculateDistance, gpsValidation, addGpsValidationFailComment, captureHandoffLocation }
